// API configuration
const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;
const NEBIUS_API_KEY = import.meta.env.VITE_NEBIUS_API_KEY;
const NEBIUS_BASE_URL = "https://api.studio.nebius.ai/v1";
const USE_NEBIUS = import.meta.env.VITE_USE_NEBIUS === 'true';
const SHOW_MODEL_INFO = import.meta.env.VITE_SHOW_MODEL_INFO !== 'false' && import.meta.env.DEV; // Show in dev mode unless explicitly disabled
const IS_DEV = import.meta.env.DEV; // Vite's built-in development mode flag

import { canMakeRequest, recordRequest } from './usageTracker';

// Models
const OPENAI_MODEL = "gpt-4o-mini";
const NEBIUS_MODEL = "meta-llama/Meta-Llama-3.1-8B-Instruct";

// Word length mapping (approximate tokens needed for the specified word count)
const wordLengthMap = {
  short: 350,    // ~250 words
  medium: 700,   // ~500 words
  long: 2100,    // ~1500 words
  xl: 2800,      // ~2000 words
  xxl: 3500      // ~2500 words
};

async function generateContent(prompt, responseLength) {
  try {
    let content;
    if (USE_NEBIUS) {
      content = await generateWithNebius(prompt, wordLengthMap[responseLength]);
    } else {
      content = await generateWithOpenAI(prompt, wordLengthMap[responseLength]);
    }

    // Show model info if enabled
    if (SHOW_MODEL_INFO) {
      const modelInfo = `[Generated using ${USE_NEBIUS ? NEBIUS_MODEL : OPENAI_MODEL}]\n\n`;
      return modelInfo + content;
    }

    return content;
  } catch (error) {
    throw error;
  }
}

export async function generateAboutUs(formData) {
  const prompt = `Please provide a response to the following request. Format your response as plain text without any markdown formatting.

Create a compelling 'About Us' page for:
Company: ${formData.businessName}
Industry: ${formData.industry}
Mission: ${formData.mission}
Values: ${formData.values || 'Not specified'}
Story: ${formData.story || 'Not specified'}
Tone: ${formData.tone || 'professional'}

Please structure the content in the following sections:
1. A captivating introduction that immediately engages the reader
2. Our Mission - highlighting the company's purpose and goals
3. Our Story - sharing the journey and key milestones
4. Our Values - emphasizing what drives the company
5. Why Choose Us - highlighting unique selling points and industry expertise
6. Closing statement that reinforces trust and encourages engagement

The content should:
- Maintain a ${formData.tone || 'professional'} tone throughout
- Be authentic and engaging
- Include specific details about the company's expertise in ${formData.industry}
- Emphasize the unique aspects of the business
- Be well-structured with clear sections
- Be concise yet comprehensive`;

  return await generateContent(prompt, 'long');
}

export async function generateInstagramReel(formData) {
  const prompt = `Please provide a response to the following request. Format your response as plain text without any markdown formatting. 
Create an engaging Instagram Reel script optimized for maximum engagement:

CORE ELEMENTS:
Key Message: ${formData.keyMessage}
Goal: ${formData.goal}
Target Audience: ${formData.targetAudience || 'General audience'}
Duration: ${formData.duration || '30 seconds'}
Tone: ${formData.tone || 'engaging'}

CREATIVE ELEMENTS:
${formData.visualIdeas ? `Visual Ideas: ${formData.visualIdeas}` : ''}
${formData.soundIdeas ? `Sound Ideas: ${formData.soundIdeas}` : ''}
${formData.hashtags ? `Suggested Hashtags: ${formData.hashtags}` : ''}

Please create a detailed Reels script that includes:

1. Hook (First 3 seconds):
   - Attention-grabbing opening
   - Clear hook to stop the scroll

2. Scene-by-Scene Breakdown:
   - Visual descriptions for each scene
   - On-screen text suggestions
   - Transitions between scenes
   - Timing for each segment

3. Audio Elements:
   - Music style recommendations
   - Sound effect suggestions
   - Voice-over script (if applicable)

4. Engagement Elements:
   - Text overlay suggestions
   - Interactive elements (polls, questions, etc.)
   - Call-to-action placement

5. Technical Notes:
   - Keep scenes dynamic and fast-paced
   - Ensure smooth transitions
   - Optimize for mobile viewing
   - Include trending elements if relevant

6. Hashtag Strategy:
   ${formData.hashtags ? 'Use the suggested hashtags strategically' : 'Recommend relevant trending hashtags'}

Total Duration: ${formData.duration || '30 seconds'}
Note: Script should be easy to follow and implement while maintaining high engagement potential.`;

  return await generateContent(prompt, 'medium');
}

export async function generateAskAnything(formData) {
  const prompt = `Please provide a response to the following request. Format your response as plain text without any markdown formatting. 

Target response length: ~${formData.responseLength === 'short' ? '250' : formData.responseLength === 'medium' ? '500' : '1500'} words.

Question/Request: ${formData.question}`;

  return await generateContent(prompt, formData.responseLength);
}

export async function generateAdCopy(formData) {
  const prompt = `Please provide a response to the following request. Format your response as plain text without any markdown formatting. 
Create compelling ad copy that drives conversions:

CORE ELEMENTS:
Product/Service: ${formData.productOrService}
Campaign Goal: ${formData.goal}
Target Audience: ${formData.targetAudience || 'General audience'}
Platform: ${formData.platform || 'Multi-platform'}
Tone: ${formData.tone || 'professional'}
CTA: ${formData.cta || 'Learn More'}

Key Features/Benefits: ${formData.keyFeatures || 'To be derived from product/service description'}

Please create a complete ad copy package that includes:

1. Headlines (3-5 variations):
   - Primary headline (max 30 characters)
   - Secondary headlines (max 40 characters)
   - Each highlighting different value propositions
   - Optimized for ${formData.platform || 'various platforms'}

2. Description/Body Copy (2-3 variations):
   - Short version (50-90 characters)
   - Long version (150-300 characters)
   - Includes key benefits
   - Uses persuasive language
   - Incorporates social proof elements

3. Call-to-Action Options:
   - Primary CTA
   - Alternative CTAs for testing
   - Urgency elements where appropriate

4. Keywords and Phrases:
   - Primary keywords
   - Related terms
   - Emotional triggers
   - Power words

5. Ad Extensions (if applicable):
   - Sitelink suggestions
   - Callout text
   - Structured snippet ideas

6. Platform-Specific Elements:
   ${formData.platform ? `
   - Optimized for ${formData.platform} best practices
   - Follows ${formData.platform} character limits
   - Uses ${formData.platform}-specific features` : `
   - Adaptable for different platforms
   - Flexible formatting options
   - Cross-platform compatibility`}

Note: Focus on benefit-driven copy, use action words, create urgency, and maintain brand voice while optimizing for the platform.`;

  return await generateContent(prompt, 'medium');
}

export async function generateArticle(formData) {
  // Convert word count option to response length
  let responseLength = 'medium'; // default
  const wordCount = formData.wordCount?.toLowerCase().split(' ')[0] || '500';
  
  if (parseInt(wordCount) <= 500) responseLength = 'medium';
  else if (parseInt(wordCount) <= 1000) responseLength = 'long';
  else if (parseInt(wordCount) <= 1500) responseLength = 'xl';
  else responseLength = 'xxl';

  const prompt = `Please provide a response to the following request. Format your response as plain text without any markdown formatting. 
Write a detailed article about the following topic. The article should be approximately ${formData.wordCount || '500 words'} in length:

Topic: ${formData.topic}
Key Points: ${formData.keyPoints}
Tone: ${formData.tone}
Target Audience: ${formData.targetAudience}
Purpose: ${formData.purpose}

Please ensure the article:
1. Is approximately ${formData.wordCount || '500 words'} in length
2. Has a clear introduction, body, and conclusion
3. Incorporates all key points naturally
4. Maintains the specified tone throughout
5. Is tailored to the target audience
6. Uses appropriate headings and subheadings
7. Includes relevant examples and evidence where appropriate`;

  return await generateContent(prompt, responseLength);
}

export async function generateBlogPost(formData) {
  const prompt = `Please provide a response to the following request. Format your response as plain text without any markdown formatting.
Write a blog post with the following details:

Topic: ${formData.topic}
Target Audience: ${formData.targetAudience}
Purpose: ${formData.purpose}
Tone: ${formData.tone}
Key Points: ${formData.keyPoints}
Word Count: ${formData.wordCount}

Please write an engaging blog post that:
1. Has a compelling introduction
2. Covers all key points thoroughly
3. Uses the specified tone
4. Maintains reader engagement
5. Includes a strong conclusion
6. Matches the requested word count
7. Aligns with the stated purpose`;

  return await generateContent(prompt, 'long');
}

export async function generateBlogPostOutline(formData) {
  const prompt = `Please provide a response to the following request. Format your response as plain text without any markdown formatting. 
Create a well-structured and engaging outline for a blog post with the following details:

Topic: ${formData.topic}
Target Audience: ${formData.targetAudience}
Goal: ${formData.goal}
Tone: ${formData.tone}
Key Points: ${formData.keyPoints}

Please create a detailed blog post outline that:
1. Has a compelling introduction section
2. Includes clear main sections and subsections
3. Follows a logical flow and progression
4. Incorporates all key points provided
5. Ends with a strong conclusion section
6. Uses appropriate headings and subheadings
7. Maintains the specified tone throughout
8. Is optimized for the target audience
9. Aligns with the blog post goal

Format the outline with clear hierarchy using:
- Main sections (I, II, III, etc.)
- Subsections (A, B, C, etc.)
- Key points under each section (1, 2, 3, etc.)
- Brief descriptions of what each section will cover`;

  return await generateContent(prompt, 'medium');
}

export async function generateEmailCampaign(formData) {
  const prompt = `Please provide a response to the following request. Format your response as plain text without any markdown formatting. 
Create an engaging and effective email campaign:

CORE ELEMENTS:
Purpose: ${formData.purpose}
Key Message: ${formData.keyMessage}
Target Audience: ${formData.targetAudience || 'General audience'}
Call to Action: ${formData.callToAction || 'Learn more'}
Tone: ${formData.tone || 'professional'}

${formData.additionalDetails ? `Additional Context: ${formData.additionalDetails}` : ''}

Please create a complete email that includes:

1. Subject Line:
   - Attention-grabbing and relevant
   - Clear value proposition
   - Under 50 characters

2. Preview Text:
   - Complementary to subject line
   - Encourages email opening
   - 40-130 characters

3. Email Body:
   - Strong opening hook
   - Clear and concise messaging
   - Scannable format with short paragraphs
   - Benefit-focused content
   - Personalized tone
   - Strategic use of bullet points or lists
   - Compelling visuals suggestions

4. Call-to-Action:
   - Clear and prominent placement
   - Action-oriented language
   - Creates urgency when appropriate

5. Email Structure:
   - Professional greeting
   - Logical flow of information
   - Strong closing
   - Professional signature
   - Unsubscribe option reminder

Note: Focus on creating value for the reader while maintaining brand voice and driving engagement.`;

  return await generateContent(prompt, 'medium');
}

export async function generateInstagramPost(formData) {
  const prompt = `Please provide a response to the following request. Format your response as plain text without any markdown formatting. 
Create an engaging Instagram post optimized for maximum engagement:

CORE ELEMENTS:
Key Message: ${formData.keyMessage}
Purpose: ${formData.purpose}
Target Audience: ${formData.targetAudience}
Tone: ${formData.tone}

ADDITIONAL DETAILS:
${formData.visuals ? `Visual Context: ${formData.visuals}` : ''}
${formData.callToAction ? `Call to Action: ${formData.callToAction}` : ''}
${formData.hashtags ? `Suggested Hashtags: ${formData.hashtags}` : ''}

Please create:
1. A captivating first line that stops the scroll
2. An engaging main caption (max 2200 characters) that:
   - Uses short, punchy paragraphs
   - Includes emojis where appropriate
   - Maintains the specified ${formData.tone} tone
   - Encourages engagement (questions, CTAs)
3. Strategic line breaks for readability
4. ${formData.hashtags ? 'A curated selection from the suggested hashtags' : 'Relevant hashtags for the content'}
5. ${formData.callToAction ? 'The specified call-to-action' : 'A natural call-to-action'}

Note: The caption should feel authentic and conversational while achieving the post's purpose.`;

  return await generateContent(prompt, 'medium');
}

export async function generateLinkedInArticle(formData) {
  const prompt = `Please provide a response to the following request. Format your response as plain text without any markdown formatting. 
  Write a LinkedIn article about:
Topic: ${formData.topic}
Key Points: ${formData.keyPoints}
Industry Focus: ${formData.industryFocus}
Target Audience: ${formData.targetAudience}
Call to Action: ${formData.callToAction}`;

  return await generateContent(prompt, 'long');
}

export async function generateProductDescription(formData) {
  const prompt = `Please provide a response to the following request. Format your response as plain text without any markdown formatting. 
Create a compelling product description that converts:

CORE ELEMENTS:
Product Name: ${formData.productName}
Key Features: ${formData.keyFeatures}
Category: ${formData.category || 'General product'}
Target Audience: ${formData.targetAudience || 'General consumers'}
Benefits: ${formData.benefits || 'To be derived from features'}
Tone: ${formData.tone || 'professional'}

${formData.additionalDetails ? `Additional Context: ${formData.additionalDetails}` : ''}

Please create a complete product description that includes:

1. Headline (10-15 words):
   - Attention-grabbing
   - Includes main value proposition
   - SEO-optimized

2. Short Description (30-50 words):
   - Quick overview
   - Main benefit or unique selling point
   - Perfect for meta descriptions

3. Main Description:
   - Compelling opening hook
   - Feature-benefit connections
   - Sensory and emotional language
   - Social proof suggestions
   - Technical specifications (if relevant)
   - Problem-solution framework
   - Scannable format with bullet points

4. Key Selling Points:
   - Unique features highlighted
   - Benefits clearly stated
   - Value propositions emphasized
   - Competitive advantages

5. Technical Details:
   - Specifications
   - Dimensions
   - Materials
   - Care instructions (if applicable)

6. Trust Elements:
   - Warranty information (if applicable)
   - Quality assurance points
   - Social proof suggestions
   - Safety certifications (if relevant)

Note: Focus on benefits over features, use persuasive language, and maintain SEO-friendly content structure.`;

  return await generateContent(prompt, 'medium');
}

export async function generateVideoScript(formData) {
  const prompt = `Please provide a response to the following request. Format your response as plain text without any markdown formatting. 
Create a video script for a ${formData.desiredLength || '2 minutes'} video with the following details:

Purpose: ${formData.purpose}
Key Message: ${formData.keyMessage}
Target Audience: ${formData.targetAudience}
Tone: ${formData.tone}
Visual Notes: ${formData.visuals}

Please structure the script with:
1. Opening hook (first ${formData.desiredLength === '30 seconds' ? '5' : '10-15'} seconds)
2. Main content sections with timing for each segment
3. Clear visual descriptions and camera directions
4. On-screen text suggestions
5. Voice-over script with pacing notes
6. Closing call-to-action
7. Total runtime should be approximately ${formData.desiredLength || '2 minutes'}

Format the script with clear timestamps and scene descriptions.`;

  // Choose token length based on video duration
  let responseLength = 'medium';
  const minutes = formData.desiredLength ? 
    parseInt(formData.desiredLength.split(' ')[0]) || 2 : 2;
  
  if (minutes <= 1) responseLength = 'medium';
  else if (minutes <= 3) responseLength = 'long';
  else responseLength = 'xl';

  return await generateContent(prompt, responseLength);
}

export async function generateTwitterThread(formData) {
  const prompt = `Create a Twitter thread about:
Topic: ${formData.topic}
Key Points: ${formData.keyPoints}
Target Audience: ${formData.targetAudience}
Tone: ${formData.tone}
Number of Tweets: ${formData.numberOfTweets}`;

  return await generateContent(prompt, 'medium');
}

export async function generateLandingPage(formData) {
  const prompt = `Please provide a response to the following request. Format your response as plain text without any markdown formatting. 
Create high-converting landing page copy:

CORE ELEMENTS:
Product/Service: ${formData.productOrService}
Primary Goal: ${formData.goal}
Target Audience: ${formData.targetAudience || 'General audience'}
Unique Value Proposition: ${formData.uniqueValue || 'To be derived from product/service description'}
Tone: ${formData.tone || 'professional'}
CTA: ${formData.cta || 'Get Started'}

Key Features/Benefits: ${formData.keyFeatures || 'To be derived from product/service description'}
${formData.additionalDetails ? `Additional Context: ${formData.additionalDetails}` : ''}

Please create a complete landing page copy package that includes:

1. Above the Fold:
   - Headline (10-15 words)
   - Subheadline (15-25 words)
   - Primary CTA
   - Social proof element suggestion

2. Value Proposition Section:
   - Main benefit statement
   - 3-4 key features with benefits
   - Supporting visuals suggestions
   - Secondary CTA placement

3. How It Works/Features:
   - Step-by-step process (if applicable)
   - Feature highlights with benefits
   - Technical specifications (if relevant)
   - Visual suggestions for each feature

4. Social Proof Section:
   - Testimonial templates
   - Statistics/results to highlight
   - Trust indicators
   - Awards/recognition suggestions

5. FAQ Section:
   - 5-7 common objection-handling questions
   - Clear, concise answers
   - Trust-building responses

6. Closing Section:
   - Final value proposition
   - Urgency/scarcity elements
   - Primary and secondary CTAs
   - Risk reducers (guarantees, trials, etc.)

7. SEO Elements:
   - Meta title suggestions
   - Meta description
   - Key phrases for headers
   - Focus keywords

Note: Focus on clear value proposition, benefit-driven copy, and strategic CTAs. Use persuasive language and maintain consistent tone throughout.`;

  return await generateContent(prompt, 'long');
}

async function generateWithOpenAI(prompt, wordCount) {
  const maxTokens = wordCount;
  try {
    const response = await fetch('/api/generate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        prompt,
        responseLength: maxTokens,
        type: 'openai'
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`API Error: ${errorData.error} ${errorData.details || ''}`);
    }

    const data = await response.json();
    return data.content;
  } catch (error) {
    console.error('Error in generateWithOpenAI:', error);
    if (error.message.includes('Failed to fetch')) {
      throw new Error('Unable to connect to the API. Please check your internet connection or try again later.');
    }
    throw error;
  }
}

async function generateWithNebius(prompt, wordCount) {
  const maxTokens = wordCount;
  try {
    // Use custom domain in production
    const baseUrl = import.meta.env.PROD 
      ? 'https://www.contentcreatorai.com'
      : 'http://localhost:3000';
    
    const response = await fetch(`${baseUrl}/api/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        prompt,
        responseLength: maxTokens,
        type: 'nebius'
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to parse error response' }));
      throw new Error(`API Error: ${errorData.error || response.statusText} ${errorData.details || ''}`);
    }

    const data = await response.json();
    return data.content;
  } catch (error) {
    console.error('Error in generateWithNebius:', error);
    if (error.message.includes('Failed to fetch')) {
      throw new Error('Unable to connect to the API. Please check your internet connection or try again later.');
    }
    throw error;
  }
}
